import { useMediaQuery } from '@components/Common/DesktopOnly';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import { Box, IconButton, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import type { WorkspaceTab } from './types';
import './index.scss';

interface EntityWorkspaceProps {
  /** Record bar */
  backLabel: string;
  onBack: () => void;
  recordIndex?: number; // 1-based position in the filtered set
  recordTotal?: number;
  onPrev?: () => void;
  onNext?: () => void;

  /** Columns */
  identityRail: React.ReactNode;
  tabs: WorkspaceTab[];
  activeTabId: string;
  onTabChange: (id: string) => void;
  /**
   * A bar above the section cards: the application stage card, or a decision waiting on the
   * record. On a phone it comes before the inline identity block, because it is what the page
   * is opened to act on and the identity would otherwise push it a screen down.
   */
  stepper?: React.ReactNode;
  children: React.ReactNode; // section cards
  activityRail: React.ReactNode;

  /** Activity rail collapse (persisted by the parent). */
  activityOpen: boolean;
  onToggleActivity: () => void;

  /** Dirty save bar */
  isDirty?: boolean;
  dirtySectionLabel?: string;
  onDiscard?: () => void;
  onSave?: () => void;
}

/**
 * Three-column entity workspace shell — replaces the detail popups.
 * Data-agnostic: entity ports supply the rails, tabs, section cards and
 * dirty-save handlers. Ships the responsive floors so the rails can never
 * eat the centre column.
 */
const EntityWorkspace: React.FC<EntityWorkspaceProps> = ({
  backLabel,
  onBack,
  recordIndex,
  recordTotal,
  onPrev,
  onNext,
  identityRail,
  tabs,
  activeTabId,
  onTabChange,
  stepper,
  children,
  activityRail,
  activityOpen,
  onToggleActivity,
  isDirty,
  dirtySectionLabel,
  onDiscard,
  onSave,
}) => {
  /**
   * Below 768px the three columns become one scroll plus tabs — nothing is
   * deleted, it is re-laid out. This tracks whether the phone-only "Activity"
   * tab is the one showing; it resets whenever the layout is wide enough to
   * have a real activity rail again.
   */
  const isPhone = useMediaQuery('(max-width: 767px)');
  const [activityTabActive, setActivityTabActive] = useState(false);
  useEffect(() => {
    if (!isPhone) setActivityTabActive(false);
  }, [isPhone]);

  // J / K move through the filtered result set without returning to the list.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (el?.isContentEditable) return;
      if (e.key === 'j' || e.key === 'J') onNext?.();
      if (e.key === 'k' || e.key === 'K') onPrev?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onNext, onPrev]);

  return (
    <Box className="cc-ws">
      {/* Record bar */}
      <Box className="cc-ws__recordbar cc-scroll-x">
        <button type="button" className="cc-ws__back" onClick={onBack}>
          <ArrowBackIcon className="cc-ws__back-icon" />
          {backLabel}
        </button>

        {(onPrev || onNext) && (
          <>
            <span className="cc-ws__divider" />
            <Box className="cc-ws__stepper-nav">
              <IconButton size="small" onClick={onPrev} aria-label="Previous record (K)">
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              {recordIndex !== undefined && recordTotal !== undefined && (
                <span className="cc-ws__record-count">
                  {recordIndex} of {recordTotal}
                </span>
              )}
              <IconButton size="small" onClick={onNext} aria-label="Next record (J)">
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </>
        )}

        <Box className="cc-ws__recordbar-spacer" />

        <Tooltip title={activityOpen ? 'Hide activity' : 'Show activity'}>
          <IconButton
            size="small"
            onClick={onToggleActivity}
            className={`cc-ws__activity-toggle${activityOpen ? ' is-active' : ''}`}
            aria-label="Toggle activity"
          >
            <ViewSidebarOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Three columns */}
      <Box className="cc-ws__cols">
        <Box className="cc-ws__rail-identity">{identityRail}</Box>

        <Box className="cc-ws__center">
          <Box className="cc-ws__tabs cc-scroll-x" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={tab.id === activeTabId && !activityTabActive}
                className={`cc-ws__tab${tab.id === activeTabId && !activityTabActive ? ' is-active' : ''}`}
                onClick={() => {
                  setActivityTabActive(false);
                  onTabChange(tab.id);
                }}
              >
                {tab.icon && <span className="cc-ws__tab-icon">{tab.icon}</span>}
                {tab.label}
              </button>
            ))}
            {/* Phone only: the activity rail has no column to live in, so it
                becomes a tab beside Overview rather than being dropped. */}
            <button
              type="button"
              role="tab"
              aria-selected={activityTabActive}
              className={`cc-ws__tab cc-ws__tab--activity${activityTabActive ? ' is-active' : ''}`}
              onClick={() => setActivityTabActive(true)}
            >
              <span className="cc-ws__tab-icon"><HistoryOutlinedIcon /></span>
              Activity
            </button>
          </Box>

          <Box className="cc-ws__center-scroll">
            {isPhone && !activityTabActive && stepper}
            {/* Phone only: identity collapses into the top of the scroll. */}
            <Box className="cc-ws__identity-inline">{identityRail}</Box>
            {activityTabActive ? (
              <Box className="cc-ws__activity-inline">{activityRail}</Box>
            ) : (
              <>
                {!isPhone && stepper}
                {children}
              </>
            )}
          </Box>

          {isDirty && (
            <Box className="cc-ws__savebar">
              <EditNoteIcon className="cc-ws__savebar-icon" />
              <span className="cc-ws__savebar-text">
                Unsaved changes in <strong>{dirtySectionLabel}</strong>
              </span>
              <Box className="cc-ws__savebar-actions">
                <button type="button" className="cc-ws__btn cc-ws__btn--ghost" onClick={onDiscard}>
                  Discard
                </button>
                <button type="button" className="cc-ws__btn cc-ws__btn--primary" onClick={onSave}>
                  Save changes
                </button>
              </Box>
            </Box>
          )}
        </Box>

        {activityOpen && (
          <Box className="cc-ws__rail-activity">{activityRail}</Box>
        )}
      </Box>
    </Box>
  );
};

export default EntityWorkspace;
